'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from './Icon'
import { NAV } from '@/lib/site'

type ExpertiseLink = { slug: string; title: string; shortTitle: string; icon: string; summary: string }

export function Header({ expertises }: { expertises: ExpertiseLink[] }) {
  const pathname = usePathname() || '/'
  const overlay = pathname === '/'
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [mobile, setMobile] = useState(false)
  const navRef = useRef<HTMLElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Ferme les menus lors d'un changement de page (ajustement d'état pendant le rendu)
  const [lastPath, setLastPath] = useState(pathname)
  if (lastPath !== pathname) {
    setLastPath(pathname)
    setOpen(null)
    setMobile(false)
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(null)
        if (mobile) {
          setMobile(false)
          toggleRef.current?.focus()
        }
      }
    }
    const onClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpen(null)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('click', onClick)
    }
  }, [mobile])

  useEffect(() => {
    document.body.style.overflow = mobile ? 'hidden' : ''
    if (mobile) closeRef.current?.focus()
  }, [mobile])

  const isActive = useCallback(
    (href: string) => (href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`)),
    [pathname],
  )

  const lightLogo = overlay
  const cls = ['site-header', overlay ? 'is-overlay' : '', scrolled ? 'is-scrolled' : ''].join(' ')

  const simple = [
    { href: '/realisations', label: 'Réalisations' },
    { href: '/references', label: 'Références' },
    { href: '/actualites', label: 'Actualités' },
    { href: '/carrieres', label: 'Carrières' },
    { href: '/contact', label: 'Contact' },
  ]

  return (
    <>
      <header className={cls}>
        <div className="container site-header__inner">
          <Link href="/" className="brand" aria-label="EXELEC — Excellence Électronique, retour à l’accueil">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={lightLogo ? '/brand/logo-horizontal-blanc.webp' : '/brand/logo-horizontal.webp'}
              alt="EXELEC — Excellence Électronique"
              width={440}
              height={127}
              fetchPriority="high"
            />
          </Link>

          <nav ref={navRef} className="nav" aria-label="Navigation principale">
            <div className="nav__item">
              <button
                type="button"
                className={`nav__link ${isActive('/entreprise') ? 'is-active' : ''}`}
                aria-expanded={open === 'entreprise'}
                aria-controls="dd-entreprise"
                onClick={() => setOpen(open === 'entreprise' ? null : 'entreprise')}
              >
                Entreprise <Icon name="chevronDown" />
              </button>
              <div id="dd-entreprise" className={`dropdown ${open === 'entreprise' ? 'is-open' : ''}`}>
                {NAV.entreprise.map((l) => (
                  <Link key={l.href} href={l.href} aria-current={pathname === l.href ? 'page' : undefined}>
                    <div>
                      <strong>{l.label}</strong>
                      <span>{l.desc}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            <div className="nav__item">
              <button
                type="button"
                className={`nav__link ${isActive('/expertises') ? 'is-active' : ''}`}
                aria-expanded={open === 'expertises'}
                aria-controls="dd-expertises"
                onClick={() => setOpen(open === 'expertises' ? null : 'expertises')}
              >
                Expertises <Icon name="chevronDown" />
              </button>
              <div id="dd-expertises" className={`dropdown dropdown--wide ${open === 'expertises' ? 'is-open' : ''}`}>
                {expertises.map((e) => (
                  <Link key={e.slug} href={`/expertises/${e.slug}`} aria-current={pathname === `/expertises/${e.slug}` ? 'page' : undefined}>
                    <span className="ico">
                      <Icon name={e.icon} />
                    </span>
                    <div>
                      <strong>{e.shortTitle}</strong>
                    </div>
                  </Link>
                ))}
                <div className="dropdown__footer">
                  <Link href="/expertises">
                    <div>
                      <strong>Toutes nos expertises →</strong>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
            {simple.map((l) => (
              <div className="nav__item" key={l.href}>
                <Link href={l.href} className="nav__link" aria-current={isActive(l.href) ? 'page' : undefined}>
                  {l.label}
                </Link>
              </div>
            ))}
          </nav>

          <div className="header-cta">
            <Link href="/contact?type=devis" className="btn btn--sm">
              Parler de votre projet
            </Link>
            <button
              ref={toggleRef}
              type="button"
              className="menu-toggle"
              aria-label="Ouvrir le menu"
              aria-expanded={mobile}
              aria-controls="mobile-nav"
              onClick={() => setMobile(true)}
            >
              <Icon name="menu" />
            </button>
          </div>
        </div>
      </header>

      <div
        id="mobile-nav"
        className={`mobile-nav ${mobile ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        aria-hidden={!mobile}
        inert={!mobile}
      >
        <div className="mobile-nav__top">
          <Link href="/" className="brand" aria-label="Accueil EXELEC">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/brand/logo-horizontal.webp" alt="EXELEC" width={440} height={127} loading="lazy" />
          </Link>
          <button ref={closeRef} type="button" className="menu-toggle" style={{ display: 'inline-flex' }} aria-label="Fermer le menu" onClick={() => setMobile(false)}>
            <Icon name="close" />
          </button>
        </div>
        <nav className="mobile-nav__body" aria-label="Navigation mobile">
          <ul>
            <li>
              <Link href="/">Accueil</Link>
            </li>
            <li>
              <details>
                <summary>
                  Entreprise <Icon name="chevronDown" />
                </summary>
                <ul>
                  {NAV.entreprise.map((l) => (
                    <li key={l.href}>
                      <Link href={l.href}>{l.label}</Link>
                    </li>
                  ))}
                </ul>
              </details>
            </li>
            <li>
              <details>
                <summary>
                  Expertises <Icon name="chevronDown" />
                </summary>
                <ul>
                  <li>
                    <Link href="/expertises">Vue d’ensemble</Link>
                  </li>
                  {expertises.map((e) => (
                    <li key={e.slug}>
                      <Link href={`/expertises/${e.slug}`}>{e.shortTitle}</Link>
                    </li>
                  ))}
                </ul>
              </details>
            </li>
            {simple.map((l) => (
              <li key={l.href}>
                <Link href={l.href}>{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="mobile-nav__foot">
          <Link href="/contact?type=devis" className="btn">
            Parler de votre projet
          </Link>
        </div>
      </div>
    </>
  )
}
